import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchEmergencyStatus, isFeatureLocked } from '@/utils/emergencyLock';
import { useUserPermissions } from '@/hooks/useUserPermissions';

const EventHistoryPage = () => {
  const { canViewEventHistory } = useUserPermissions();
  const [events, setEvents] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!canViewEventHistory) return;

    const fetchEvents = async () => {
      try {
        const res = await axios.get('/api/events/history');
        setEvents(res.data || []);
      } catch (err) {
        console.error('Error loading events:', err);
      }
    };

    const checkLock = async () => {
      const status = await fetchEmergencyStatus();
      setIsLocked(isFeatureLocked(status, ['cyberattack', 'offline']));
    };

    fetchEvents();
    checkLock();
  }, [canViewEventHistory]);

  if (!canViewEventHistory) {
    return (
      <div className="text-red-500 p-6">
        <h2 className="text-xl font-bold">🚫 Access Denied</h2>
        <p>You do not have permission to view event history.</p>
      </div>
    );
  }

  const handleExport = async (type) => {
    try {
      const endpoint = type === 'csv' ? '/api/events/export-csv' : '/api/events/export-pdf';
      const res = await axios.get(endpoint, { responseType: 'blob' });

      const fileURL = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = fileURL;
      link.setAttribute('download', `EventHistory.${type}`);
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error('Export error:', err);
      setMessage('Failed to export report.');
    }
  };

  return (
    <div className="bg-neutral-900 min-h-screen p-6 text-white">
      <h2 className="text-2xl font-bold mb-4">📅 Event History</h2>

      {isLocked ? (
        <div className="bg-red-600 text-white p-3 rounded-lg font-medium text-center mb-4">
          🚫 Export disabled due to Emergency Mode.
        </div>
      ) : (
        <div className="flex gap-4 mb-6">
          <button onClick={() => handleExport('csv')} className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-xl font-semibold">Export CSV</button>
          <button onClick={() => handleExport('pdf')} className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-xl font-semibold">Export PDF</button>
        </div>
      )}

      <div className="overflow-auto rounded-xl border border-neutral-700">
        <table className="w-full text-sm">
          <thead className="bg-neutral-800">
            <tr>
              <th className="p-3 text-left">Date</th>
              <th>Match</th>
              <th>Type</th>
              <th>Total Staff</th>
              <th>Total Cost</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id} className="border-t border-neutral-700 hover:bg-neutral-800">
                <td className="p-3">{event.date}</td>
                <td>{event.match_name}</td>
                <td>{event.match_type}</td>
                <td>{event.total_staff}</td>
                <td>£{event.total_cost}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {message && <p className="mt-4 text-yellow-400 font-medium">{message}</p>}
    </div>
  );
};

export default EventHistoryPage;
